// Checked module declarations (composition framework rule 4: features never import each other, they meet at declared
// seams; tier map "Modules and composition"; decision 0077). A module may declare what it `provides` (events it emits,
// services it offers, HUD panels it adds) and what it `needs` (events it listens for and can't work without, services
// it must have for real). `createGame` checks the set before any module's setup runs: a needed event or service that
// nothing provides, or a service or HUD panel provided twice. Under a harness (fail-fast) that throws a
// DeclarationError; in play it logs one clear line per problem and the game boots.
//
// Declarations are optional: a module without them takes no part in the check, so spike 001's and older modules keep
// working. Only declare a need when its provider declares too (or the host lists it in `hostProvides`).
import type { EventName, IsOpenEvents, OpenEvents } from "./event-bus.js";

/** An event name a module may declare: any string on an open bus, a name of the map on a typed one. */
export type DeclaredEvent<M extends object = OpenEvents> = IsOpenEvents<M> extends true ? string : EventName<M>;

/** What a module offers. */
export interface ModuleProvides<M extends object = OpenEvents> {
  /** Events it emits. Many modules may emit one event. */
  events?: readonly DeclaredEvent<M>[];
  /** Service keys it offers (a `ServiceKey`'s `key`). One provider per key. */
  services?: readonly string[];
  /** HUD panel ids it adds (through a HUD panel slot). One owner per id. */
  hudPanels?: readonly string[];
}

/** What a module can't work without. */
export interface ModuleNeeds<M extends object = OpenEvents> {
  /** Events it listens for that some module (or the host) must emit. */
  events?: readonly DeclaredEvent<M>[];
  /** Service keys some module (or the host, through `createGame`'s providers) must offer. */
  services?: readonly string[];
}

/** A module's declarations, as the check reads them. */
export interface Declared {
  id: string;
  provides?: { events?: readonly string[]; services?: readonly string[]; hudPanels?: readonly string[] };
  needs?: { events?: readonly string[]; services?: readonly string[] };
}

/** What the host (the game's own wiring outside modules: its view, its boot) provides. */
export interface HostProvides {
  events?: readonly string[];
  services?: readonly string[];
  hudPanels?: readonly string[];
}

/** One problem the check found. */
export interface DeclarationIssue {
  /** The module it is about. */
  module: string;
  kind: "event-unprovided" | "service-unprovided" | "service-twice" | "panel-twice";
  /** The event, service or panel. */
  name: string;
  message: string;
}

/** What `createGame` throws under a fail-fast policy when the declarations don't add up. */
export class DeclarationError extends Error {
  constructor(readonly issues: readonly DeclarationIssue[]) {
    super(`[kernel] module declarations don't add up (${issues.length}):\n  ${issues.map((i) => i.message).join("\n  ")}`);
    this.name = "DeclarationError";
  }
}

/** Planted defects for proving the declaration check can fail (engine/CLAUDE.md). Off by default. */
export const DECLARATION_FAULTS = {
  /** Skip the check: a needed event nothing provides goes unnoticed (the kernel's test must go red). */
  skipCheck: false,
};

/** Check a set of modules' declarations, with what the host provides. Empty when they add up. */
export function checkDeclarations(modules: readonly Declared[], host: HostProvides = {}): DeclarationIssue[] {
  if (DECLARATION_FAULTS.skipCheck) return [];
  const issues: DeclarationIssue[] = [];
  const events = new Set<string>(host.events ?? []);
  const services = new Map<string, string>();
  const panels = new Map<string, string>();
  for (const s of host.services ?? []) services.set(s, "the host");
  for (const p of host.hudPanels ?? []) panels.set(p, "the host");
  for (const m of modules) {
    for (const e of m.provides?.events ?? []) events.add(e);
    for (const s of m.provides?.services ?? []) {
      const owner = services.get(s);
      if (owner !== undefined) issues.push({ module: m.id, kind: "service-twice", name: s, message: `module "${m.id}" provides service "${s}", which ${owner === "the host" ? "the host" : `module "${owner}"`} already provides` });
      else services.set(s, m.id);
    }
    for (const p of m.provides?.hudPanels ?? []) {
      const owner = panels.get(p);
      if (owner !== undefined) issues.push({ module: m.id, kind: "panel-twice", name: p, message: `module "${m.id}" adds HUD panel "${p}", which ${owner === "the host" ? "the host" : `module "${owner}"`} already adds` });
      else panels.set(p, m.id);
    }
  }
  for (const m of modules) {
    for (const e of m.needs?.events ?? []) {
      if (!events.has(e)) issues.push({ module: m.id, kind: "event-unprovided", name: e, message: `module "${m.id}" needs event "${e}", which no module (and not the host) provides` });
    }
    for (const s of m.needs?.services ?? []) {
      if (!services.has(s)) issues.push({ module: m.id, kind: "service-unprovided", name: s, message: `module "${m.id}" needs service "${s}", which no module (and not the host) provides` });
    }
  }
  return issues;
}
